import React, { Component } from "react";
import {
  PageHeader,
  Row,
  Col,
  Form,
  Table,
  Tag,
  Button,
  Checkbox,
  Radio,
  DatePicker,
  Modal,
  Empty,
  notification,
} from "antd";
import AgentAttendanceService from "../../services/agentattendanceservice";
import { authHeader } from "../../services/auth-header";
import moment from "moment";

/**
 * Ops review of delivery-agent attendance.
 *
 * The verdict is computed server-side (ReviewReasons / NeedsReview); this screen
 * presents it. Two deliberate choices:
 *  - Flagged marks sort FIRST and "only flagged" defaults ON. A reviewer who has
 *    to hunt for problems eventually stops looking.
 *  - The selfie is fetched through the API with the bearer token, never a static
 *    URL. A staff photograph must not be reachable by guessing.
 */
export default class AgentAttendance extends Component {
  constructor(props) {
    super(props);
    this.service = new AgentAttendanceService();
    this.state = {
      fromDate: moment(),
      toDate: moment(),
      onlyFlagged: true,
      rows: [],
      loading: false,
      photoFor: null,
      photoSrc: null,
      photoMsg: "",
      view: "daily",
      month: moment(),
      monthlyRows: [],
      monthLoading: false,
    };
  }

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.setState({ loading: true });
    let res;
    try {
      res = await this.service.getForReview(
      this.state.fromDate ? this.state.fromDate.format("YYYY-MM-DD") : null,
      this.state.toDate ? this.state.toDate.format("YYYY-MM-DD") : null,
      null,
        this.state.onlyFlagged
      );
    } catch (e) {
      res = { message: (e && e.message) || "Request failed" };
    }
    // finally-style: the spinner must never outlive the request, whatever happened.
    if (Array.isArray(res)) {
      this.setState({ rows: res, loading: false });
    } else {
      this.setState({ rows: [], loading: false });
      notification.error({
        message: "Could not load attendance",
        description:
          (res && (res.message || res.statusCode
            ? `${res.message || "Server said"} (status ${res.statusCode || "unknown"})`
            : null)) || "The server did not return a list.",
        duration: 0,
      });
    }
  };

  openPhoto = async (row) => {
    this.setState({ photoFor: row, photoSrc: null, photoMsg: "Loading…" });
    try {
      const res = await fetch(this.service.photoUrl(row.attendanceID), {
        headers: authHeader(),
      });
      if (res.status === 404) {
        // Normal, not an error: photos are recycled after 45 days by design.
        this.setState({ photoMsg: "No photo — not taken, or recycled after 45 days." });
        return;
      }
      if (!res.ok) {
        this.setState({ photoMsg: "Could not load the photo." });
        return;
      }
      const blob = await res.blob();
      this.setState({ photoSrc: URL.createObjectURL(blob), photoMsg: "" });
    } catch (e) {
      this.setState({ photoMsg: "Could not load the photo." });
    }
  };

  closePhoto = () => {
    if (this.state.photoSrc) URL.revokeObjectURL(this.state.photoSrc);
    this.setState({ photoFor: null, photoSrc: null, photoMsg: "" });
  };

  fmt = (v) => (v ? moment(v).format("DD MMM, HH:mm") : "—");

  distance = (m) => {
    if (m === null || m === undefined) return "—";
    return m >= 1000 ? (m / 1000).toFixed(1) + " km" : m + " m";
  };

  columns = [
    {
      title: "Agent",
      dataIndex: "agentName",
      render: (t, r) => (
        <span>
          <strong>{t && t.trim() ? t : "User " + r.userID}</strong>
          <br />
          <small style={{ color: "#888" }}>
            {r.mobileNo || ""} {r.deviceModel ? "· " + r.deviceModel : ""}
          </small>
        </span>
      ),
    },
    {
      title: "In / Out",
      dataIndex: "inTime",
      render: (t, r) => (
        <span>
          {this.fmt(t)}
          <br />
          <small style={{ color: "#888" }}>
            {r.outTime ? this.fmt(r.outTime) : "still on duty"}
          </small>
        </span>
      ),
    },
    {
      title: "Start",
      dataIndex: "startMode",
      render: (t, r) =>
        t === "ENROUTE" ? (
          <span>
            <Tag color="purple">En route</Tag>
            <br />
            <small style={{ color: "#888" }}>claimed {r.claimedPickupID}</small>
          </span>
        ) : (
          <Tag>At store</Tag>
        ),
    },
    {
      title: "Location",
      dataIndex: "locationSource",
      render: (t, r) => (
        <span>
          {t === "GPS" ? <Tag color="green">GPS</Tag> : <Tag color="orange">{t || "—"}</Tag>}
          {r.locationAccuracyM ? (
            <div>
              <small style={{ color: "#888" }}>±{Math.round(r.locationAccuracyM)} m</small>
            </div>
          ) : null}
          {r.isMockLocation ? <Tag color="red">Mock</Tag> : null}
        </span>
      ),
    },
    {
      title: "From store",
      dataIndex: "metresFromStore",
      align: "right",
      render: (m) => this.distance(m),
    },
    {
      title: "Photo",
      dataIndex: "hasPhoto",
      render: (has, r) =>
        has ? (
          <Button size="small" onClick={() => this.openPhoto(r)}>
            View
          </Button>
        ) : r.photoPurgedOn ? (
          <small style={{ color: "#888" }}>Recycled</small>
        ) : (
          <small style={{ color: "#cf1322" }}>None</small>
        ),
    },
    {
      title: "Needs a look",
      dataIndex: "reviewReasons",
      render: (t, r) =>
        r.needsReview ? (
          <span style={{ color: "#cf1322" }}>{t}</span>
        ) : (
          <span style={{ color: "#389e0d" }}>Looks fine</span>
        ),
    },
  ];

  switchView = (v) => {
    this.setState({ view: v }, () => {
      if (v === "monthly" && this.state.monthlyRows.length === 0) this.loadMonthly();
    });
  };

  loadMonthly = async () => {
    this.setState({ monthLoading: true });
    const m = this.state.month || moment();
    let res;
    try {
      res = await this.service.getMonthly(m.year(), m.month() + 1, 0);
    } catch (e) {
      res = { message: (e && e.message) || "Request failed" };
    }
    if (Array.isArray(res)) {
      this.setState({ monthlyRows: res, monthLoading: false });
    } else {
      this.setState({ monthlyRows: [], monthLoading: false });
      notification.error({ message: "Could not load monthly attendance", duration: 0 });
    }
  };

  // Per-day cell status. Wednesday is the weekly off -> shown grey, NOT counted absent.
  dayStatus = (row, day) => {
    const m = this.state.month || moment();
    const date = moment(m).date(day);
    if (date.isAfter(moment(), "day")) return "future";
    const joined = row.joinedOn ? moment(row.joinedOn) : null;
    if (joined && date.isBefore(joined, "day")) return "nojoin";
    if ((row._pd || []).indexOf(day) !== -1) return "present";
    if (date.day() === 3) return "off";
    return "absent";
  };

  renderMonthly = () => {
    const m = this.state.month || moment();
    const days = m.daysInMonth();
    const rows = (this.state.monthlyRows || []).map((r) => ({
      ...r,
      _pd: (r.presentDays || "")
        .split(",")
        .map((x) => parseInt(x, 10))
        .filter((x) => !isNaN(x)),
    }));
    const wd = (d) => "SMTWTFS"[moment(m).date(d).day()];
    const isSun = (d) => moment(m).date(d).day() === 0;
    const dayArr = Array.from({ length: days }, (_, i) => i + 1);
    return (
      <div className="amc" style={{ padding: "0 24px 24px" }}>
        <style>{`
          .amc .ctrls{display:flex;gap:10px;align-items:center;margin:2px 0 12px;flex-wrap:wrap}
          .amc .legend{display:flex;gap:14px;align-items:center;margin-left:auto;font-size:12px;color:#4b5563;flex-wrap:wrap}
          .amc .lg{display:inline-flex;align-items:center;gap:5px}.amc .sw{width:13px;height:13px;border-radius:3px;display:inline-block}
          .amc .scroll{overflow-x:auto;border:1px solid #eef0f4;border-radius:10px}
          .amc table{border-collapse:separate;border-spacing:0;font-size:11px;width:100%}
          .amc th,.amc td{padding:0;text-align:center}
          .amc .agent{position:sticky;left:0;z-index:2;background:#fff;text-align:left;padding:7px 12px;min-width:168px;border-right:1px solid #eef0f4}
          .amc thead .agent{background:#f7f7fb;border-bottom:1px solid #eef0f4}
          .amc .agent .nm{font-weight:600;font-size:12.5px;color:#111827}.amc .agent .dv{color:#9096a1;font-size:10.5px;margin-top:1px}
          .amc thead th{background:#f7f7fb;color:#6b7280;font-weight:600;border-bottom:1px solid #eef0f4;padding:5px 0;min-width:23px}
          .amc thead th .wd{display:block;font-size:9px;color:#aeb3bd;font-weight:600}.amc thead th.sun .wd{color:#e0a3a3}
          .amc td.cell{height:26px;border-bottom:1px solid #f3f4f7}.amc td.sun{background:#fcfbfc}
          .amc .box{width:17px;height:17px;border-radius:4px;margin:0 auto;display:flex;align-items:center;justify-content:center;font-size:9px;font-weight:700}
          .amc .present{background:#dcfce7;color:#15803d}.amc .absent{background:#fee2e2;color:#b91c1c}
          .amc .off,.amc .nojoin{background:#f1f2f5;color:#c3c7cf}.amc .future{background:transparent}
          .amc .sumh{background:#f7f7fb;border-bottom:1px solid #eef0f4;color:#6b7280;font-weight:600;padding:5px 8px;border-left:1px solid #eef0f4}
          .amc td.sum{padding:6px 8px;border-bottom:1px solid #f3f4f7;border-left:1px solid #eef0f4;font-variant-numeric:tabular-nums;font-weight:700;font-size:12px}
          .amc td.sum.first,.amc .sumh.first{border-left:2px solid #e6e7ee}
          .amc .pill{padding:1px 6px;border-radius:20px;font-size:11px}.amc .g{background:#dcfce7;color:#15803d}.amc .y{background:#fef9c3;color:#a16207}.amc .r{background:#fee2e2;color:#b91c1c}
          .amc .foot{margin-top:11px;font-size:11.5px;color:#6b7280;line-height:1.5}
        `}</style>
        <div className="ctrls">
          <DatePicker
            picker="month"
            format="MMMM YYYY"
            allowClear={false}
            value={this.state.month}
            onChange={(d) => this.setState({ month: d || moment() }, this.loadMonthly)}
          />
          <Button type="primary" onClick={this.loadMonthly} loading={this.state.monthLoading}>
            Show
          </Button>
          <div className="legend">
            <span className="lg"><span className="sw" style={{ background: "#dcfce7" }} />Present</span>
            <span className="lg"><span className="sw" style={{ background: "#fee2e2" }} />Absent</span>
            <span className="lg"><span className="sw" style={{ background: "#f1f2f5" }} />Off (Wed) / not joined</span>
          </div>
        </div>
        {!this.state.monthLoading && rows.length === 0 ? (
          <Empty description="No agent attendance for this month." />
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th className="agent">Agent</th>
                  {dayArr.map((d) => (
                    <th key={d} className={isSun(d) ? "sun" : ""}>
                      <span className="wd">{wd(d)}</span>
                      {d}
                    </th>
                  ))}
                  <th className="sumh first">P</th>
                  <th className="sumh">A</th>
                  <th className="sumh">%</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  let pres = 0,
                    abs = 0;
                  const cells = dayArr.map((d) => {
                    const s = this.dayStatus(r, d);
                    if (s === "present") pres++;
                    if (s === "absent") abs++;
                    return (
                      <td key={d} className={"cell " + (isSun(d) ? "sun" : "")}>
                        <div className={"box " + s}>
                          {s === "present" ? "P" : s === "absent" ? "A" : ""}
                        </div>
                      </td>
                    );
                  });
                  const tot = pres + abs;
                  const pct = tot ? Math.round((pres / tot) * 100) : 0;
                  const pill = pct >= 90 ? "g" : pct >= 75 ? "y" : "r";
                  return (
                    <tr key={r.userID}>
                      <td className="agent">
                        <div className="nm">
                          {r.agentName && r.agentName.trim() ? r.agentName : "User " + r.userID}
                        </div>
                        <div className="dv">
                          {(r.deviceModel || "").trim()}
                          {r.mobileNo ? " · " + r.mobileNo : ""}
                        </div>
                      </td>
                      {cells}
                      <td className="sum first">{pres}</td>
                      <td className="sum">{abs}</td>
                      <td className="sum">
                        <span className={"pill " + pill}>{pct}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="foot">
          <b>Wednesday</b> is the weekly off (grey, not counted absent). Days before an agent&#39;s
          first mark-in show grey (not joined). <b>Present</b> = at least one mark-in that day; <b>%</b> =
          present ÷ (present + absent).
        </div>
      </div>
    );
  };

  render() {
    const { rows, loading, photoFor, photoSrc, photoMsg } = this.state;
    // Flagged first — a reviewer should meet the problems before the routine days.
    const sorted = [...rows].sort(
      (a, b) => (b.needsReview ? 1 : 0) - (a.needsReview ? 1 : 0)
    );
    const flagged = rows.filter((r) => r.needsReview).length;

    return (
      <div>
        <PageHeader
          title="Agent attendance"
          subTitle="Selfie and location for every delivery agent who marked in"
        />

        <div style={{ padding: "0 24px 6px" }}>
          <Radio.Group
            value={this.state.view}
            onChange={(e) => this.switchView(e.target.value)}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="daily">Daily marks</Radio.Button>
            <Radio.Button value="monthly">Monthly calendar</Radio.Button>
          </Radio.Group>
        </div>

        {this.state.view === "daily" && (
          <React.Fragment>
        <Form layout="vertical" style={{ padding: "0 24px" }}>
          <Row gutter={16} align="bottom">
            <Col xs={24} sm={6}>
              <Form.Item label="From">
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  value={this.state.fromDate}
                  onChange={(d) => this.setState({ fromDate: d })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="To">
                <DatePicker
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  value={this.state.toDate}
                  onChange={(d) => this.setState({ toDate: d })}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label=" " colon={false}>
                <Checkbox
                  checked={this.state.onlyFlagged}
                  onChange={(e) =>
                    this.setState({ onlyFlagged: e.target.checked }, this.load)
                  }
                >
                  Only marks that need a look
                </Checkbox>
              </Form.Item>
            </Col>
            <Col xs={24} sm={4}>
              <Form.Item label=" " colon={false}>
                <Button type="primary" onClick={this.load} loading={loading} block>
                  Show
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <div style={{ padding: "0 24px 8px", color: "#888" }}>
          {!loading && rows.length > 0
            ? `${rows.length} mark${rows.length === 1 ? "" : "s"}` +
              (flagged > 0 ? ` · ${flagged} need a look` : " · nothing flagged")
            : null}
        </div>

        <div style={{ padding: "0 24px 24px" }}>
          <Table
            size="small"
            rowKey="attendanceID"
            loading={loading}
            columns={this.columns}
            dataSource={sorted}
            pagination={{ pageSize: 25, hideOnSinglePage: true }}
            rowClassName={(r) => (r.needsReview ? "ant-table-row-flagged" : "")}
            onRow={(r) => ({
              style: r.needsReview ? { background: "#fffbe6" } : {},
            })}
            locale={{
              emptyText: (
                <Empty
                  description={
                    this.state.onlyFlagged
                      ? "Nothing flagged for these dates. Untick the box to see every mark."
                      : "No agent marked in on these dates."
                  }
                />
              ),
            }}
          />
        </div>
          </React.Fragment>
        )}

        {this.state.view === "monthly" && this.renderMonthly()}

        <Modal
          visible={!!photoFor}
          title={
            photoFor
              ? (photoFor.agentName && photoFor.agentName.trim()
                  ? photoFor.agentName
                  : "User " + photoFor.userID) +
                " · " +
                this.fmt(photoFor.inTime)
              : ""
          }
          footer={null}
          onCancel={this.closePhoto}
        >
          <div style={{ textAlign: "center" }}>
            {photoSrc ? (
              <img src={photoSrc} alt="Attendance selfie" style={{ maxWidth: "100%" }} />
            ) : (
              <p style={{ color: "#888" }}>{photoMsg}</p>
            )}
            {photoFor && photoFor.capturedOn ? (
              <p style={{ color: "#888", marginTop: 12, marginBottom: 0 }}>
                <small>
                  Taken {this.fmt(photoFor.capturedOn)} · uploaded{" "}
                  {this.fmt(photoFor.uploadedOn)}
                  {photoFor.uploadDelayMin > 15
                    ? ` (${photoFor.uploadDelayMin} min later)`
                    : ""}
                </small>
              </p>
            ) : null}
          </div>
        </Modal>
      </div>
    );
  }
}
